'use client';

import { useState, useEffect } from 'react';
import { JobResponse, VideoJobCreate } from '@/lib/types';
import api from '@/lib/api';
import { useToast } from './Toast';

interface VideoGenerateModalProps {
  sourceJob: JobResponse;
  onClose: () => void;
  onSuccess: () => void;
}

type VideoModel = 'ltx-2' | 'kandinsky5';

const MODEL_PRESETS: Record<VideoModel, {
  label: string;
  description: string;
  resolutions: { width: number; height: number; label: string }[];
  frameOptions: { value: number; label: string }[];
  defaultWidth: number;
  defaultHeight: number;
  defaultFrames: number;
  fps: number;
}> = {
  'ltx-2': {
    label: 'LTX-2.3',
    description: '22B model with audio generation',
    resolutions: [
      { width: 512, height: 512, label: '512x512 (1:1)' },
      { width: 768, height: 512, label: '768x512 (3:2)' },
      { width: 512, height: 768, label: '512x768 (2:3)' },
      { width: 832, height: 480, label: '832x480 (16:9)' },
      { width: 1024, height: 576, label: '1024x576 (16:9 HD)' },
    ],
    frameOptions: [
      { value: 25, label: '25 (~1s)' },
      { value: 49, label: '49 (~2s)' },
      { value: 97, label: '97 (~4s)' },
      { value: 121, label: '121 (~5s)' },
    ],
    defaultWidth: 768,
    defaultHeight: 512,
    defaultFrames: 97,
    fps: 25,
  },
  'kandinsky5': {
    label: 'Kandinsky5',
    description: 'ComfyUI workflow (requires ComfyUI)',
    resolutions: [
      { width: 512, height: 320, label: '512x320' },
      { width: 640, height: 448, label: '640x448' },
      { width: 768, height: 512, label: '768x512' },
      { width: 1024, height: 768, label: '1024x768' },
    ],
    frameOptions: [
      { value: 49, label: '49 (~2s)' },
      { value: 73, label: '73 (~3s)' },
      { value: 97, label: '97 (~4s)' },
      { value: 121, label: '121 (~5s)' },
    ],
    defaultWidth: 768,
    defaultHeight: 512,
    defaultFrames: 121,
    fps: 24,
  },
};

export default function VideoGenerateModal({
  sourceJob,
  onClose,
  onSuccess
}: VideoGenerateModalProps) {
  const [model, setModel] = useState<VideoModel>('ltx-2');
  const [prompt, setPrompt] = useState('');
  const [resolution, setResolution] = useState('768x512');
  const [frames, setFrames] = useState(MODEL_PRESETS['ltx-2'].defaultFrames);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  const preset = MODEL_PRESETS[model];
  const [w, h] = resolution.split('x').map(Number);

  // Reset to defaults when model changes
  useEffect(() => {
    const p = MODEL_PRESETS[model];
    setResolution(`${p.defaultWidth}x${p.defaultHeight}`);
    setFrames(p.defaultFrames);
  }, [model]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) {
      toast.error('Please enter a motion description');
      return;
    }

    setIsSubmitting(true);
    try {
      const request: VideoJobCreate = {
        prompt: prompt.trim(),
        source_job_id: sourceJob.job_id,
        width: w,
        height: h,
        frames,
        model,
      };
      await api.createVideoJob(request);
      toast.success('Video generation job created! Check Videos page.');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to create video job:', error);
      toast.error('Failed to create video job: ' + (error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const duration = (frames / preset.fps).toFixed(1);

  return (
    <div className="fixed inset-0 z-[60] bg-[var(--bg-primary)] animate-fadeIn flex items-center justify-center p-4">
      <div className="bg-[var(--bg-elevated)] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border border-[var(--border-subtle)]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)]">
          <h2 className="text-xl font-semibold">Generate Video</h2>
          <button
            onClick={onClose}
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)] rounded-lg transition-all cursor-pointer"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Source image preview */}
          <div className="flex gap-4 mb-6">
            <div className="w-32 h-32 rounded-lg overflow-hidden bg-[var(--bg-primary)] flex-shrink-0">
              {sourceJob.images[0] && (
                <img
                  src={api.getThumbnailUrl(sourceJob.images[0])}
                  alt="Source"
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[var(--text-muted)] mb-1">Source Image</p>
              <p className="text-sm truncate">{sourceJob.prompt}</p>
              <p className="text-xs text-[var(--text-muted)] mt-2">
                {sourceJob.width}x{sourceJob.height}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Model selector */}
            <div>
              <label className="block text-sm font-medium mb-2">Model</label>
              <div className="flex gap-2">
                {(Object.keys(MODEL_PRESETS) as VideoModel[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setModel(m)}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      model === m
                        ? 'bg-[var(--accent)] text-black'
                        : 'bg-[var(--bg-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    {MODEL_PRESETS[m].label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-[var(--text-muted)] mt-1">{preset.description}</p>
            </div>

            {/* Motion prompt */}
            <div>
              <label className="block text-sm font-medium mb-2">Motion Description</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe the motion or action (e.g., 'Camera slowly zooms in while steam rises from the cup')"
                className="w-full px-4 py-3 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none resize-none"
                rows={3}
                autoFocus
              />
            </div>

            {/* Resolution + Frames */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Resolution</label>
                <select
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none cursor-pointer"
                >
                  {preset.resolutions.map((r) => (
                    <option key={`${r.width}x${r.height}`} value={`${r.width}x${r.height}`}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Duration</label>
                <select
                  value={frames}
                  onChange={(e) => setFrames(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none cursor-pointer"
                >
                  {preset.frameOptions.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Duration info */}
            <div className="bg-[var(--bg-primary)] rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm">
                <svg className="w-5 h-5 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                </svg>
                <span className="text-[var(--text-muted)]">
                  ~{duration}s at {preset.fps}fps &middot; {w}x{h} &middot; {preset.label}
                </span>
              </div>
              <p className="text-xs text-[var(--text-muted)] mt-2">
                {model === 'ltx-2'
                  ? 'First generation loads model (~10-15 min). Subsequent: ~2-6 min.'
                  : 'Requires ComfyUI connection. Generation takes 2-10 minutes.'}
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[var(--border-subtle)]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !prompt.trim()}
            className="px-6 py-2 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {isSubmitting ? 'Creating...' : 'Generate Video'}
          </button>
        </div>
      </div>
    </div>
  );
}
